import { ThunkActionCreator } from "../interfaces/thunk";
import { Dispatch } from "../interfaces/dispatch";
import axios from "axios";
import Config from "../config";
import { getHeaders } from "../helpers/headers";
import { handleActions } from "redux-actions";
import { AnyAction } from "../interfaces/action";
import { FIRST_PAGE, PAGE_SIZE } from "helpers/values";

// AbortController for cancelling previous requests to prevent race conditions
let segmentationController: AbortController | null = null;

function getCancelableSegmentationAxios() {
  if (segmentationController) {
    segmentationController.abort();
  }
  segmentationController = new AbortController();
  return { signal: segmentationController.signal };
}

export const FETCH_FILTER_INFO_REQUEST = "FETCH_FILTER_INFO_REQUEST";
export const FETCH_FILTER_INFO = "FETCH_FILTER_INFO";
export const FETCH_FILTER_INFO_FAILED = "FETCH_FILTER_INFO_FAILED";
export const FETCH_SEGMENTATION_REPORT_REQUEST = "FETCH_SPACE_SUMMARY_REQUEST";
export const FETCH_SEGMENTATION_REPORT = "FETCH_SPACE_SUMMARY";
export const FETCH_SEGMENTATION_REPORT_FAILED = "FETCH_SPACE_SUMMARY_FAILED";
export const FETCH_COMPARISON_SEGMENTATION_REPORT_REQUEST =
  "FETCH_COMPARISON_SEGMENTATION_REPORT_REQUEST";
export const UPDATE_SEGMENTATION_TYPE = "UPDATE_SEGMENTATION_TYPE";

const fetchSegmentationFilters: ThunkActionCreator<Promise<void>> = () => (dispatch: Dispatch) => {
  dispatch({ type: FETCH_FILTER_INFO_REQUEST });
  return axios
    .get(`${Config.API_SERVICE_URL}/api/reports/segmentation/dropdowns`, getHeaders())
    .then((response) => {
      dispatch({ type: FETCH_FILTER_INFO, payload: response.data });
    })
    .catch((_) => {
      dispatch({ type: FETCH_FILTER_INFO_FAILED });
    });
};

const fetchSegmentationInfo: ThunkActionCreator<Promise<void>> =
  ({
    id,
    comparisonId,
    segmentationType,
    storeIds = [],
    page,
    size,
    search,
    sort
  }: {
    id: number;
    comparisonId: number;
    segmentationType: string;
    storeIds: number[];
    page: number;
    size: number;
    search?: string;
    sort?: string;
  }) =>
  (dispatch: Dispatch) => {
    const { signal } = getCancelableSegmentationAxios();
    dispatch({ type: FETCH_SEGMENTATION_REPORT_REQUEST });
    const params = new URLSearchParams();
    let storeIdsParam = "";
    if (segmentationType) {
      params.set("segmentationType", segmentationType);
    }
    if (Array.isArray(storeIds) && storeIds.length > 0) {
      storeIdsParam = storeIds.join(",");
    }
    if (page !== undefined) {
      params.set("page", page.toString());
    }
    if (size !== undefined) {
      params.set("size", size.toString());
    }
    if (search) {
      params.set("search", search);
    }
    if (sort) {
      params.set("sort", sort);
    }

    return axios
      .post(
        `${Config.API_SERVICE_URL}/api/reports/segmentation/${id}/${
          comparisonId > -1 ? comparisonId : ""
        }?${params.toString()}`,
        {
          storeIds: storeIdsParam
        },
        { ...getHeaders(), signal }
      )
      .then((response) => {
        dispatch({ type: FETCH_SEGMENTATION_REPORT, payload: response.data });
      })
      .catch((err) => {
        if (err.name === "CanceledError") {
          console.log("Previous segmentation report request cancelled");
        } else {
        dispatch({ type: FETCH_SEGMENTATION_REPORT_FAILED });
        }
      });
  };

const updateSegmentationType = (segmentationType: string) => ({
  type: UPDATE_SEGMENTATION_TYPE,
  payload: segmentationType,
});

const actions = {
  [FETCH_FILTER_INFO_REQUEST]: (state: SegmentationReportState) => ({
    ...state,
    loading: true,
  }),
  [FETCH_FILTER_INFO]: (
    state: SegmentationReportState,
    action: AnyAction
  ): SegmentationReportState => ({
    ...state,
    loading: false,
    filters: { ...action.payload },
  }),
  [FETCH_FILTER_INFO_FAILED]: (state: SegmentationReportState) => ({
    ...state,
    loading: false,
    error: true,
    filters: undefined,
  }),
  [FETCH_SEGMENTATION_REPORT_REQUEST]: (state: SegmentationReportState) => ({
    ...state,
    tableLoading: true,
  }),
  [FETCH_SEGMENTATION_REPORT]: (
    state: SegmentationReportState,
    action: AnyAction
  ): SegmentationReportState => ({
    ...state,
    tableLoading: false,
    content: { ...action.payload, count: action.payload.count || state.content.count },
  }),
  [FETCH_SEGMENTATION_REPORT_FAILED]: (state: SegmentationReportState) => ({
    ...state,
    tableLoading: false,
    error: true,
    content: {
      projectId: "",
      comparisonId: null,
      size: PAGE_SIZE,
      page: FIRST_PAGE,
      segmentationType: "",
      segmentationReport: [],
      count: 0
    },
  }),

  [FETCH_COMPARISON_SEGMENTATION_REPORT_REQUEST]: (state: SegmentationReportState) => ({
    ...state,
    loading: true,
  }),
  [UPDATE_SEGMENTATION_TYPE]: (
    state: SegmentationReportState,
    action: AnyAction
  ): SegmentationReportState => ({
    ...state,
    segmentationType: action.payload,
  }),
};

export interface SegmentationReportState {
  content: {
    projectId: string;
    comparisonId: string | null;
    size: number;
    page: number;
    segmentationType: string;
    count: number;
    segmentationReport: any;
  };
  error: boolean;
  loading: boolean;
  tableLoading: boolean;
  filters: any;
  comparisonData: any;
  segmentationType: string;
}

const initialState: SegmentationReportState = {
  loading: false,
  tableLoading: false,
  error: false,
  content: {
    projectId: "",
    comparisonId: null,
    size: PAGE_SIZE,
    page: FIRST_PAGE,
    segmentationType: "",
    count: 0,
    segmentationReport: [],
  },
  filters: {},
  comparisonData: {},
  segmentationType: "subcategory",
};

export { fetchSegmentationFilters, fetchSegmentationInfo, updateSegmentationType };

export default handleActions<SegmentationReportState, AnyAction>(actions, initialState);
